import { extractPlaceholders } from '@/utils/placeholders';
import type {
  DayOfWeekName,
  DesktopNotificationScheduleDto,
  DesktopNotificationType,
} from '@/api/types/desktopNotification.types';

export type NotificationReminderScheduleRow = DesktopNotificationScheduleDto;

export interface NotificationReminderFormState {
  // null until the admin picks one on create; on edit it is always the persisted, locked type.
  type: DesktopNotificationType | null;
  name: string;
  enabled: boolean;
  dismissEnabled: boolean;
  schedules: NotificationReminderScheduleRow[];
  timeZone: string;
  header: string;
  body: string;
  audience: {
    grades: string[];
    geos: string[];
    staffIds: number[];
    excludedStaffIds: number[];
    completionRateMinPercent: number | null;
    completionRateMaxPercent: number | null;
  };
}

const TIME_PATTERN = /^\d{2}:\d{2}(:\d{2})?$/;

export function validateNotificationForm(
  form: NotificationReminderFormState,
  allowedTokens: string[],
): string | null {
  if (!form.type) {
    return 'Type is required.';
  }
  if (!form.name.trim()) {
    return 'Name is required.';
  }
  if (form.name.length > 200) {
    return 'Name must be 200 characters or less.';
  }

  if (form.schedules.length === 0) {
    return 'Add at least one schedule day.';
  }
  const seenDays = new Set<DayOfWeekName>();
  for (const row of form.schedules) {
    if (seenDays.has(row.dayOfWeek)) {
      return 'Each day can only appear once in the schedule.';
    }
    seenDays.add(row.dayOfWeek);

    if (!row.timeOfDay || !TIME_PATTERN.test(row.timeOfDay)) {
      return 'Each schedule day requires a valid time.';
    }
    if (row.snoozeDurationMinutes !== null && row.snoozeDurationMinutes <= 0) {
      return 'Snooze duration must be a positive number of minutes.';
    }
  }

  // The server rejects a Happiness Survey without exactly one anchor with a 400, so catch it here and
  // name the control the admin has to touch.
  if (
    form.type === 'HappinessSurvey' &&
    form.schedules.filter((row) => row.isGenerationAnchor).length !== 1
  ) {
    return 'Mark exactly one schedule day as the survey generation day.';
  }

  if (!form.timeZone.trim()) {
    return 'Time zone is required.';
  }
  if (!form.header.trim()) {
    return 'Header is required.';
  }
  if (form.header.length > 500) {
    return 'Header must be 500 characters or less.';
  }
  if (!form.body.trim()) {
    return 'Body is required.';
  }
  if (form.body.length > 4000) {
    return 'Body must be 4000 characters or less.';
  }

  const { completionRateMinPercent: min, completionRateMaxPercent: max } = form.audience;
  if (min !== null && (min < 0 || min > 100)) {
    return 'Completion rate minimum must be between 0 and 100.';
  }
  if (max !== null && (max < 0 || max > 100)) {
    return 'Completion rate maximum must be between 0 and 100.';
  }
  if (min !== null && max !== null && min > max) {
    return 'Completion rate minimum cannot exceed the maximum.';
  }

  const usedTokens = [
    ...extractPlaceholders(form.header),
    ...extractPlaceholders(form.body),
  ];
  const unknown = Array.from(new Set(usedTokens.filter((t) => !allowedTokens.includes(t))));
  if (unknown.length > 0) {
    return `Unknown placeholder(s): ${unknown.map((t) => `'${t}'`).join(', ')}.`;
  }

  return null;
}
